<template>
	<v-container style="margin-top: 10px;">
		<p>
			NIS Cyber Security scores are measured on a scale from 0 to 100.
			This map shows the average <strong>{{ label }}</strong> score for the
			countries of <strong>your connections</strong>.
			When multiple suppliers share a country, each supplier gets its own
			vertical slice (lowest score on the left, highest on the right).
		</p>
		<p style="font-weight: bold;" class="mb-2">
			Source: Orixe NIS Cyber Security assessments from your connections.
		</p>

		<div class="map-legend mb-4">
			<span class="legend-item">
				<span class="legend-dot legend-dot--green"></span>
				Green ≥ 70
			</span>
			<span class="legend-separator">·</span>
			<span class="legend-item">
				<span class="legend-dot legend-dot--yellow"></span>
				Yellow 40–69
			</span>
			<span class="legend-separator">·</span>
			<span class="legend-item">
				<span class="legend-dot legend-dot--red"></span>
				Red &lt; 40
			</span>
		</div>

		<v-card flat outlined v-if="mapCountries.length">
			<supplier-performance-world-map :countries="mapCountries" />
		</v-card>
		<p v-else class="mt-4">No connection NIS scores available yet.</p>

		<div v-if="mapSuppliers.length" class="map-supplier-summary mt-4">
			<p class="map-supplier-summary-title mb-2">
				{{ mapSuppliers.length }} suppliers · {{ mapCountries.length }}
				{{ mapCountries.length === 1 ? "country" : "countries" }}
			</p>
			<ul class="map-supplier-summary-list mb-0 pl-0">
				<li
					v-for="supplier in mapSuppliers"
					:key="supplier.clientID || `${supplier.name}-${supplier.country}`"
					class="map-supplier-summary-item"
				>
					<span
						class="legend-dot"
						:style="{ background: supplier.fill }"
					></span>
					<strong>{{ supplier.name }}</strong>
					<span class="map-supplier-summary-meta">
						{{ supplier.country }} · {{ label }} {{ supplier.score }}
						<span v-if="!supplier.mapId"> · not shown on map</span>
					</span>
				</li>
			</ul>
		</div>
	</v-container>
</template>

<script>
import iso3166 from "iso-3166-1"
import NisApi from "../api/nis/nis"
import SupplierPerformanceWorldMap from "../components/SupplierPerformanceWorldMap.vue"
import LoadingState from "../consts/LoadingState"
import { MAP_TIER_COLORS } from "../utils/connectionMapChart"

function nisTier(score) {
	if (score === null || score === undefined || !Number.isFinite(Number(score))) {
		return null
	}
	const value = Number(score)
	if (value >= 70) return "green"
	if (value >= 40) return "yellow"
	return "red"
}

function resolveCountryMapId(country) {
	if (!country) return null
	const trimmed = String(country).trim()
	if (!trimmed || trimmed === "Undefined") return null

	let iso = iso3166.whereCountry(trimmed)
	if (iso?.alpha2) return iso.alpha2.toLowerCase()

	if (/^[a-z]{2}$/i.test(trimmed)) {
		iso = iso3166.whereAlpha2(trimmed.toUpperCase())
		if (iso?.alpha2) return iso.alpha2.toLowerCase()
		return trimmed.toLowerCase()
	}

	if (/^[a-z]{3}$/i.test(trimmed)) {
		iso = iso3166.whereAlpha3(trimmed.toUpperCase())
		if (iso?.alpha2) return iso.alpha2.toLowerCase()
	}

	return null
}

export default {
	name: "NisConnectionMap",
	components: { SupplierPerformanceWorldMap },
	props: {
		scoreKey: {
			type: String,
			default: "overall",
		},
		label: {
			type: String,
			default: "Overall",
		},
	},
	data() {
		return {
			raw: [],
			mapCountries: [],
			mapSuppliers: [],
		}
	},
	watch: {
		scoreKey() {
			this.buildMap()
		},
	},
	mounted() {
		this.loadItems()
	},
	methods: {
		readScore(nis, key) {
			if (!nis) return null
			const raw = nis[key]
			if (raw == null || raw === "") return null
			const value = Number(raw)
			if (!Number.isFinite(value)) return null
			return Math.round(value)
		},
		scoreFor(entry) {
			if (!entry || !entry.nis) return null
			if (this.scoreKey === "overall") {
				const vals = [
					this.readScore(entry.nis, "score1"),
					this.readScore(entry.nis, "score2"),
					this.readScore(entry.nis, "score3"),
					this.readScore(entry.nis, "score4"),
					this.readScore(entry.nis, "score5"),
					this.readScore(entry.nis, "score6"),
				].filter((s) => s !== null)
				if (!vals.length) return null
				return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
			}
			return this.readScore(entry.nis, this.scoreKey)
		},
		buildMap() {
			const byMapId = {}
			const suppliers = []

			for (const entry of this.raw) {
				const score = this.scoreFor(entry)
				const country = entry.COO || "Undefined"
				const mapId = resolveCountryMapId(country)
				const tier = nisTier(score)
				const scoreLabel =
					score === null || score === undefined ? "No NIS data" : `${score}%`

				suppliers.push({
					clientID: entry.clientID ?? null,
					name: entry.name,
					country: country === "Undefined" ? "Unknown country" : country,
					mapId,
					score: scoreLabel,
					tier,
					fill: tier ? MAP_TIER_COLORS[tier] : MAP_TIER_COLORS.default,
				})

				if (!mapId || score === null) continue

				if (!byMapId[mapId]) {
					byMapId[mapId] = {
						id: mapId,
						name: country,
						total: 0,
						count: 0,
						suppliers: [],
					}
				}

				byMapId[mapId].total += score
				byMapId[mapId].count += 1
				byMapId[mapId].suppliers.push({
					name: entry.name,
					score: scoreLabel,
					clientID: entry.clientID ?? null,
					rawScore: score,
					tier,
				})
			}

			this.mapCountries = Object.values(byMapId).map((entry) => {
				const average = Math.round(entry.total / entry.count)
				const tier = nisTier(average)
				const scoredSuppliers = entry.suppliers.filter(
					(supplier) => supplier.tier && supplier.rawScore !== null
				)

				let fillMode = "solid"
				let splitSlices = null
				if (scoredSuppliers.length >= 2) {
					fillMode = "split"
					splitSlices = [...scoredSuppliers]
						.sort((a, b) => a.rawScore - b.rawScore)
						.map((supplier) => ({
							name: supplier.name,
							score: supplier.score,
							fill: MAP_TIER_COLORS[supplier.tier],
						}))
				}

				return {
					id: entry.id,
					name: entry.name,
					averageLabel: `${average}%`,
					tier,
					fillMode,
					splitSlices,
					fill:
						fillMode === "split"
							? splitSlices[0].fill
							: tier
								? MAP_TIER_COLORS[tier]
								: MAP_TIER_COLORS.default,
					suppliers: entry.suppliers.map(({ name, score, clientID }) => ({
						name,
						score,
						clientID,
					})),
				}
			})

			this.mapSuppliers = suppliers
		},
		loadItems() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			NisApi.getNisSuppliers()
				.then((data) => {
					this.raw = data || []
					this.buildMap()
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
	},
}
</script>

<style scoped>
.map-legend {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px;
	font-size: 0.875rem;
	color: #616161;
}

.legend-item {
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.legend-separator {
	color: #bdbdbd;
}

.legend-dot {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	flex-shrink: 0;
}

.legend-dot--green {
	background: #2e7d32;
}

.legend-dot--yellow {
	background: #f9a825;
}

.legend-dot--red {
	background: #c62828;
}

.map-supplier-summary-title {
	font-size: 0.875rem;
	font-weight: 600;
	color: #424242;
}

.map-supplier-summary-list {
	list-style: none;
}

.map-supplier-summary-item {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 0.8125rem;
	color: #616161;
	line-height: 1.6;
}

.map-supplier-summary-meta {
	color: #757575;
}
</style>
